import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Switch,
  Tooltip,
  CircularProgress,
  Collapse,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Rating,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Sidebar from '../../Pages/landingPage/Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import {
  Person4,
  SettingsRounded,
  TrackChanges,
  Business,
  ContactMail,
  RateReview,
  Announcement,
} from '@mui/icons-material';
import { AdminTabs } from '../AdminTabs';
import Cookies from 'js-cookie';
import { getPublicReviews, approvePublicReview, sendReviewResponseEmail } from '../../apis/Admin';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, type: 'spring', stiffness: 100 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.3, ease: 'easeOut' },
  }),
};

const thSx = {
  fontSize: '0.75rem',
  fontWeight: 500,
  py: 1,
  px: 2,
  color: '#ffffff',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #E5E7EB',
};

const tdIndexSx = {
  py: 0.25,
  px: 2,
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#6b7280',
  verticalAlign: 'middle',
};

const tdTextSx = () => ({ py: 0.25, px: 2 });

const Pill = ({ children, color }) => (
  <Box
    sx={{
      display: 'inline-block',
      px: 1.25,
      py: 0.35,
      borderRadius: '10px',
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.02em',
      color,
      bgcolor: alpha(color, 0.12),
      textTransform: 'capitalize',
    }}
  >
    {children}
  </Box>
);

Pill.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

export default function AdminPublicReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [approvingId, setApprovingId] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showApprovedList, setShowApprovedList] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [responseReview, setResponseReview] = useState(null);
  const [responseSubject, setResponseSubject] = useState('');
  const [responseText, setResponseText] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Super admin check
  const rawRole = useMemo(
    () => Cookies.get('Role_id') ?? Cookies.get('role_id'),
    [],
  );

  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const reviewsData = await getPublicReviews({
        is_approved: showApprovedList,
      });
      // Handle different response formats
      const reviewsList = Array.isArray(reviewsData)
        ? reviewsData
        : Array.isArray(reviewsData?.data)
          ? reviewsData.data
          : Array.isArray(reviewsData?.reviews)
            ? reviewsData.reviews
            : [];
      setReviews(reviewsList);
      setPageError(null);
    } catch (error) {
      console.error('Error fetching public reviews:', error);
      setPageError('Error fetching public reviews data');
    } finally {
      setLoading(false);
    }
  }, [showApprovedList]);

  useEffect(() => {
    if (!isSuperAdmin) {
      window.location.href = '/admin/user';
      return;
    }
    fetchReviews();
  }, [isSuperAdmin, fetchReviews]);

  const handleApproveToggle = async (reviewId, currentApproved) => {
    setApprovingId(reviewId);
    try {
      await approvePublicReview(reviewId, !currentApproved);
      // Update local state
      setReviews((prev) =>
        prev.map((review) =>
          review.id === reviewId || review.review_id === reviewId
            ? {
                ...review,
                approved: !currentApproved,
                is_approved: !currentApproved,
              }
            : review,
        ),
      );
    } catch (error) {
      console.error('Error approving review:', error);
      setPageError('Failed to update review approval status');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRowClick = (review) => {
    setSelectedReview(review);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReview(null);
  };

  const handleOpenResponseDialog = (review, e) => {
    e.stopPropagation();
    setResponseReview(review);
    setResponseSubject(`Response to your Review - ${review.name || 'Review'}`);
    setResponseText('');
    setResponseDialogOpen(true);
  };

  const handleCloseResponseDialog = () => {
    setResponseDialogOpen(false);
    setResponseReview(null);
    setResponseSubject('');
    setResponseText('');
  };

  const handleSendResponse = async () => {
    if (!responseSubject.trim() || !responseText.trim()) {
      toast.error('Please fill in both subject and response fields');
      return;
    }

    if (!responseReview?.email) {
      toast.error('Review email not found');
      return;
    }

    setSendingEmail(true);
    try {
      await sendReviewResponseEmail({
        to_emails: [responseReview.email],
        subject: responseSubject.trim(),
        body: responseText.trim(),
        is_html: false,
      });
      toast.success('Response email sent successfully!');
      handleCloseResponseDialog();
    } catch (error) {
      console.error('Error sending response email:', error);
      toast.error(
        error.message || 'Failed to send email. Please try again.',
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const filtered = useMemo(() => {
    const safeReviews = Array.isArray(reviews) ? reviews : [];
    const q = search.trim().toLowerCase();

    if (!q) return safeReviews;

    return safeReviews.filter((review) => {
      const name = (review.name || '').toLowerCase();
      const email = (review.email || '').toLowerCase();
      const org = (review.organization || '').toLowerCase();
      const reviewText = (review.review_text || '').toLowerCase();

      return (
        name.includes(q) ||
        email.includes(q) ||
        org.includes(q) ||
        reviewText.includes(q)
      );
    });
  }, [reviews, search]);

  // Redirect if not super admin (after all hooks are called)
  useEffect(() => {
    if (!isSuperAdmin) {
      window.location.href = '/admin/user';
    }
  }, [isSuperAdmin]);

  // Early return after all hooks (conditional rendering)
  if (!isSuperAdmin) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          bgcolor: '#f8fafc',
          minHeight: '100vh',
        }}
      >
        <Sidebar />
        <Box sx={{ flexGrow: 1, px: 3, py: 2 }}>
          <Collapse in={!!pageError} unmountOnExit>
            <Alert
              severity="error"
              onClose={() => setPageError(null)}
              sx={{
                mb: 2,
                border: '1px solid #fecaca',
                background: '#fee2e2',
                color: '#7f1d1d',
              }}
            >
              {pageError}
            </Alert>
          </Collapse>

          <AdminTabs
            items={[
              {
                to: '/admin/organizations',
                label: 'Organizations',
                icon: Business,
              },
              { to: '/admin/user', label: 'User Management', icon: Person4 },
              {
                to: '/admin/annoucement',
                label: 'Annoucement',
                icon: Announcement,
              },
              {
                to: '/admin/setting',
                label: 'Application Settings',
                icon: SettingsRounded,
              },
              {
                to: '/admin/audit-log',
                label: 'Audit Logs',
                icon: TrackChanges,
              },
              {
                to: '/admin/contact-submissions',
                label: 'Contact Submissions',
                icon: ContactMail,
              },
              {
                to: '/admin/public-reviews',
                label: 'Public Reviews',
                icon: RateReview,
              },
            ]}
          />

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: '1.5rem',
                  }}
                >
                  Public Reviews
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mr: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        color: '#6b7280',
                        fontWeight: 500,
                      }}
                    >
                      Approved list
                    </Typography>
                    <Switch
                      checked={showApprovedList}
                      onChange={(e) => setShowApprovedList(e.target.checked)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#5965A6',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                          {
                            backgroundColor: '#5965A6',
                          },
                      }}
                    />
                  </Box>
                  <Collapse in={searchOpen} orientation="horizontal">
                    <TextField
                      size="small"
                      placeholder="Search reviews..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              sx={{ color: '#9ca3af', fontSize: '1.2rem' }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: search && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearch('')}
                              sx={{ p: 0.5 }}
                            >
                              <CloseRounded sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: searchOpen ? 280 : 0,
                        transition: 'width 0.3s ease',
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                        },
                      }}
                    />
                  </Collapse>
                  <IconButton
                    onClick={() => setSearchOpen(!searchOpen)}
                    sx={{
                      backgroundColor: searchOpen ? '#5965A6' : '#e5e7eb',
                      color: searchOpen ? '#ffffff' : '#6b7280',
                      '&:hover': {
                        backgroundColor: searchOpen ? '#4C5B9A' : '#d1d5db',
                      },
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Box>
              </Box>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                }}
              >
                {loading && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 4,
                    }}
                  >
                    <CircularProgress size={24} sx={{ color: '#5965A6' }} />
                  </Box>
                )}

                <TableContainer
                  sx={{ overflow: 'hidden', paddingBottom: '1rem' }}
                >
                  <Table
                    size="small"
                    sx={{
                      width: '100%',
                      '& .MuiTableCell-root': {
                        py: 0.5,
                        px: 2,
                        borderBottom: '1px solid #e5e7eb',
                      },
                      '& thead .MuiTableCell-root': { py: 0.5, px: 1.25 },
                      '& .MuiTableRow-root': { height: 44 },
                    }}
                  >
                    <TableHead sx={{ backgroundColor: '#5965A6' }}>
                      <TableRow
                        sx={{
                          '&:hover': { backgroundColor: '#4C5B9A' },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <TableCell sx={thSx}>Sr No</TableCell>
                        <TableCell sx={thSx}>Name</TableCell>
                        <TableCell sx={thSx}>Email</TableCell>
                        <TableCell sx={thSx}>Job Title</TableCell>
                        <TableCell sx={thSx}>Rating</TableCell>
                        <TableCell sx={thSx}>Review</TableCell>
                        <TableCell sx={thSx}>Actions</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filtered.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            align="center"
                            sx={{ py: 4 }}
                          >
                            <motion.div
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.3 }}
                            >
                              <Typography
                                sx={{
                                  color: '#64748b',
                                  fontSize: '0.95rem',
                                  mb: 1,
                                }}
                              >
                                {loading
                                  ? 'Loading reviews...'
                                  : 'No reviews found.'}
                              </Typography>
                            </motion.div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <AnimatePresence>
                          {filtered.map((review, i) => {
                            const reviewId = review.id || review.review_id;
                            const isApproved =
                              review.approved || review.is_approved || false;
                            return (
                              <Tooltip
                                key={reviewId}
                                title="Click to see more in details"
                                placement="top"
                                arrow
                              >
                                <TableRow
                                  component={motion.tr}
                                  custom={i}
                                  variants={rowVariants}
                                  initial="hidden"
                                  animate="visible"
                                  onClick={() => handleRowClick(review)}
                                  sx={{
                                    borderBottom: '1px solid #F3F4F6',
                                    backgroundColor:
                                      i % 2 === 1 ? '#F3F5F7' : '#ffffff',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      backgroundColor:
                                        i % 2 === 1 ? '#71B9FF40' : '#71B9FF40',
                                      transform: 'scale(1.001)',
                                      boxShadow:
                                        '0 2px 8px rgba(89, 101, 166, 0.15)',
                                    },
                                  }}
                                >
                                  <TableCell sx={tdIndexSx}>{i + 1}</TableCell>
                                  <TableCell sx={tdTextSx()}>
                                    {review.name || 'N/A'}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      py: 0.25,
                                      px: 2,
                                      fontSize: '0.875rem',
                                      color: '#6b7280',
                                    }}
                                  >
                                    {review.email || 'N/A'}
                                  </TableCell>
                                  <TableCell sx={tdTextSx()}>
                                    {review.organization || 'N/A'}
                                  </TableCell>
                                  <TableCell sx={{ py: 0.25, px: 2 }}>
                                    <Pill color="#FF8C00">
                                      <Box
                                        component="span"
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                        }}
                                      >
                                        {review.rating || 0}
                                        <Box
                                          component="span"
                                          sx={{
                                            color: '#FF8C00',
                                            fontSize: '1rem',
                                            lineHeight: 1,
                                          }}
                                        >
                                          ⭐
                                        </Box>
                                      </Box>
                                    </Pill>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      py: 0.25,
                                      px: 2,
                                      fontSize: '0.875rem',
                                      color: '#6b7280',
                                      maxWidth: '300px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {review.review_text || 'No review text'}
                                  </TableCell>

                                  <TableCell
                                    sx={{ py: 0.25, px: 2 }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      {!showApprovedList && (
                                        <Tooltip
                                          title={
                                            isApproved ? 'Approved' : 'Approve'
                                          }
                                        >
                                          <Switch
                                            checked={isApproved}
                                            onChange={() =>
                                              handleApproveToggle(
                                                reviewId,
                                                isApproved,
                                              )
                                            }
                                            disabled={approvingId === reviewId}
                                            size="small"
                                            sx={{
                                              '& .MuiSwitch-switchBase.Mui-checked':
                                                {
                                                  color: '#5965A6',
                                                },
                                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                                {
                                                  backgroundColor: '#5965A6',
                                                },
                                            }}
                                          />
                                        </Tooltip>
                                      )}
                                      <Tooltip title="Send Response" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={(e) =>
                                            handleOpenResponseDialog(review, e)
                                          }
                                          sx={{
                                            color: '#5965A6',
                                            '&:hover': {
                                              backgroundColor: '#5965A620',
                                              color: '#4C5B9A',
                                            },
                                          }}
                                        >
                                          <ReplyIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              </Tooltip>
                            );
                          })}
                        </AnimatePresence>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </motion.div>
          </motion.div>
        </Box>
      </Box>

      {/* Review Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            width: '600px',
            minHeight: '400px',
            maxHeight: '70vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            pb: 0,
            pt: 1.5,
            px: 2,
          }}
        >
          <IconButton
            onClick={handleCloseDialog}
            size="small"
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f3f4f6',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 1,
            pb: 3,
            px: 3,
            minHeight: '350px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {selectedReview && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}
            >
              {/* Name */}
              <Typography
                variant="h6"
                sx={{
                  color: '#1e293b',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                {selectedReview.name || 'N/A'}
              </Typography>

              {/* Job Title */}
              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  fontSize: '0.9rem',
                  fontWeight: 400,
                  mb: 1,
                }}
              >
                {selectedReview.organization || 'N/A'}
              </Typography>

              {/* Rating */}
              <Rating
                value={selectedReview.rating || 0}
                readOnly
                precision={0.5}
                icon={
                  <StarIcon sx={{ color: '#FFD700', fontSize: '1.5rem' }} />
                }
                emptyIcon={
                  <StarIcon sx={{ color: '#e0e0e0', fontSize: '1.5rem' }} />
                }
                sx={{
                  mb: 1.5,
                  '& .MuiRating-iconFilled': {
                    color: '#FFD700',
                  },
                }}
              />

              {/* Review Message */}
              <Typography
                variant="body1"
                sx={{
                  color: '#374151',
                  fontSize: '0.95rem',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {selectedReview.review_text || 'No review text provided.'}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Response Dialog */}
      <Dialog
        open={responseDialogOpen}
        onClose={handleCloseResponseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            maxWidth: '500px',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #5965A6 0%, #4C5B9A 100%)',
            color: '#ffffff',
            py: 1.5,
            px: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReplyIcon sx={{ fontSize: '1.25rem' }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1.1rem',
              }}
            >
              Send Response to Review
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseResponseDialog}
            size="small"
            sx={{
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {responseReview && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Review Info */}
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#6b7280',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    mb: 0.75,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Review Details
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#1f2937', fontWeight: 500, mb: 0.25, fontSize: '0.8rem' }}
                >
                  From: {responseReview.name || 'N/A'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#6b7280', fontSize: '0.8rem', mb: 0.25 }}
                >
                  Email: {responseReview.email || 'N/A'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#6b7280', fontSize: '0.8rem' }}
                >
                  Organization: {responseReview.organization || 'N/A'}
                </Typography>
                {responseReview.rating && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: '#6b7280', fontSize: '0.8rem' }}
                    >
                      Rating:
                    </Typography>
                    <Rating
                      value={responseReview.rating}
                      readOnly
                      size="small"
                      icon={
                        <StarIcon
                          sx={{ color: '#FFD700', fontSize: '1rem' }}
                        />
                      }
                      emptyIcon={
                        <StarIcon
                          sx={{ color: '#e0e0e0', fontSize: '1rem' }}
                        />
                      }
                    />
                  </Box>
                )}
              </Box>

              {/* Subject Field */}
              <TextField
                fullWidth
                size="small"
                label="Subject"
                value={responseSubject}
                onChange={(e) => setResponseSubject(e.target.value)}
                placeholder="Enter email subject"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                    },
                    '&:hover fieldset': {
                      borderColor: '#5965A6',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5965A6',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#5965A6',
                  },
                }}
              />

              {/* Response Text Field */}
              <TextField
                fullWidth
                size="small"
                multiline
                rows={5}
                label="Response"
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Enter your response message..."
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                    },
                    '&:hover fieldset': {
                      borderColor: '#5965A6',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5965A6',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#5965A6',
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
          }}
        >
          <Button
            onClick={handleCloseResponseDialog}
            disabled={sendingEmail}
            size="small"
            sx={{
              color: '#6b7280',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: '#f3f4f6',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendResponse}
            disabled={sendingEmail || !responseSubject.trim() || !responseText.trim()}
            variant="contained"
            size="small"
            startIcon={
              sendingEmail ? (
                <CircularProgress size={14} sx={{ color: '#ffffff' }} />
              ) : (
                <SendIcon fontSize="small" />
              )
            }
            sx={{
              backgroundColor: '#5965A6',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 0.75,
              fontSize: '0.875rem',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(89, 101, 166, 0.3)',
              '&:hover': {
                backgroundColor: '#4C5B9A',
                boxShadow: '0 4px 12px rgba(89, 101, 166, 0.4)',
              },
              '&:disabled': {
                backgroundColor: '#d1d5db',
                color: '#9ca3af',
              },
            }}
          >
            {sendingEmail ? 'Sending...' : 'Send Response'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
